import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Archive,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Crosshair,
  ExternalLink,
  Import,
  LayoutTemplate,
  ListChecks,
  Loader2,
  Pencil,
  PlayCircle,
  Plus,
  RefreshCcw,
  Tag,
  Target,
  Trash2,
  Trophy,
} from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { BulkBar, RowBox, SelectAllBox, useTableSelection } from "../../components/TableSelection";
import { Badge, Button, EventTypeChip, Modal, Pagination, Select, StatCard, statusTone } from "../../components/ui";
import { adminSession, apiDelete, apiPost, apiPut, useApi } from "../../api/client";
import { Competition, CompetitionEvent, MatchInfo } from "../../data/types";

const emptyForm: Partial<MatchInfo> = {
  name: "",
  eventId: "e1",
  competitionId: "npr-2024",
  date: "",
  distance: "1000 Yards",
  shots: 10,
  strings: 2,
  scoring: "10 to 1",
  timeLimit: "10 Minutes",
  status: "UPCOMING",
  liveScoring: false,
};

export default function AdminMatches() {
  const [params] = useSearchParams();
  const { data: allMatches, reload } = useApi<MatchInfo[]>("/matches", []);
  const { data: competitions } = useApi<Competition[]>("/competitions", []);
  const { data: events } = useApi<CompetitionEvent[]>("/events", []);
  const { data: stats } = useApi<{ results: number } | null>("/stats", null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  /*
   * The match shown in the detail panel. Clicking a row used to navigate away
   * to the results page, which reads as the Matches page vanishing into the
   * Competitions section with no way back; the panel meanwhile always showed
   * the first row. Selecting in place is what the panel was for.
   */
  const [selectedId, setSelectedId] = useState("");
  // Clicking a competition on the Competitions page lands here pre-filtered.
  const [compId, setCompId] = useState(params.get("competitionId") || "");

  const q = search.trim().toLowerCase();
  const matches = allMatches.filter(
    (m) =>
      (!q || `${m.name} ${m.id} ${m.competitionId} ${m.eventId}`.toLowerCase().includes(q)) &&
      (statusFilter === "All Statuses" || m.status === statusFilter.toUpperCase()) &&
      (!compId || m.competitionId === compId)
  );

  // Selection follows the filtered list, so "select all" after filtering by
  // competition targets exactly that competition's matches.
  const selection = useTableSelection(matches.map((m) => m.id));
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<MatchInfo>>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k: keyof MatchInfo, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const compName = (id: string) => competitions.find((c) => c.id === id)?.name ?? id;

  /*
   * Nothing stops two shoots being named the same — a club that runs the same
   * meeting twice in a season produces exactly that. So the filter carries the
   * competition id, and a name that appears more than once is shown with its id
   * so the two are told apart in the list.
   */
  const competitionOptions = useMemo(() => {
    const seen = new Map<string, number>();
    competitions.forEach((c) => seen.set(c.name, (seen.get(c.name) || 0) + 1));
    return competitions.map((c) => ({
      value: c.id,
      label: (seen.get(c.name) || 0) > 1 ? `${c.name} (${c.id})` : c.name,
    }));
  }, [competitions]);
  /*
   * The competition a range is shot for — "Bronger", "Carrington" — read from
   * the match's CID against the event's Competition sheet.
   *
   * This line used to read "Event 3: 6 Sept 2026", naming each shoot day as an
   * event of its own. A meeting is ONE event however many days it runs, and
   * national teams are selected on matches within a competition, so a day
   * labelled as an event puts the wrong thing in front of a selector.
   */
  const competitionName = (m: MatchInfo) => {
    const comp = competitions.find((c) => c.id === m.competitionId);
    const declared = comp?.championships ?? [];
    if (!m.cid) return "";
    const named = declared.find((x) => String(x.cid) === String(m.cid));
    return named ? named.name : `Competition ${m.cid}`;
  };

  // The day a range was shot on, which is a date rather than a level of its own.
  const dayLabel = (id: string) => {
    const e = events.find((x) => x.id === id);
    return e ? `Day ${e.n} · ${e.name}` : "";
  };
  const detail = matches.find((m) => m.id === selectedId) || matches[0];

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
    setOpen(true);
  };
  const openEdit = (m: MatchInfo) => {
    setForm(m);
    setEditingId(m.id);
    setError("");
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.eventId || !form.competitionId) {
      setError("Name, event and competition are required.");
      return;
    }
    if (!adminSession.token()) {
      setError("Sign in first (admin / admin123) — changes are authenticated.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingId) await apiPut(`/admin/matches/${editingId}`, form);
      else await apiPost("/admin/matches", form);
      setOpen(false);
      reload();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  /*
   * One code path for one row or many — a single delete is a pending list of
   * one — so the confirmation, progress and error handling are identical
   * however it was started.
   */
  const [pending, setPending] = useState<MatchInfo[]>([]);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const removeSelected = async () => {
    setRemoving(true);
    const failed: string[] = [];
    for (const m of pending) {
      try {
        await apiDelete(`/admin/matches/${m.id}`);
      } catch (e: any) {
        failed.push(`${m.name}: ${e.message}`);
      }
    }
    setRemoving(false);
    setPending([]);
    selection.clear();
    reload();
    setNotice(
      failed.length
        ? `${pending.length - failed.length} of ${pending.length} removed. ${failed[0]}`
        : `${pending.length} match${pending.length === 1 ? "" : "es"} removed.`
    );
  };

  const liveCount = allMatches.filter((m) => m.status === "LIVE" || m.status === "IN PROGRESS").length;
  const completedCount = allMatches.filter((m) => m.status === "COMPLETED").length;
  const archivedCount = allMatches.filter((m) => m.status === "ARCHIVED").length;

  return (
    <div>
      <AdminTopbar
        title="Matches"
        subtitle="Manage all matches, strings and results."
        actions={
          <>
            <Button onClick={openCreate}><Plus size={16} /> Create Match</Button>
            <Link to="/admin/import"><Button variant="secondary"><Import size={16} /> Import Results</Button></Link>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={<Crosshair size={20} />} value={allMatches.length} label="Total Matches" sub="All Time" />
        <StatCard icon={<PlayCircle size={20} />} value={liveCount} label="Live / In Progress" sub="Currently" iconBg="bg-success-soft text-success" />
        <StatCard icon={<CheckCircle2 size={20} />} value={completedCount} label="Completed" sub="All Time" iconBg="bg-violet-50 text-violet-600" />
        <StatCard icon={<Archive size={20} />} value={archivedCount} label="Archived" sub="All Time" iconBg="bg-warning-soft text-warning" />
        <StatCard icon={<BarChart3 size={20} />} value={(stats?.results ?? 0).toLocaleString()} label="Result Rows" sub="All scopes" />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <div className="card flex flex-wrap items-end gap-3 p-4">
            <div className="min-w-[280px] flex-1">
              <label className="label">Search</label>
              <input
                className="input"
                placeholder="Search matches by name, competition or event..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Event</label>
              {/* Keyed on the competition id: two shoots can share a name. */}
              <Select
                options={[{ value: "", label: "All Events" }, ...competitionOptions]}
                value={compId}
                onChange={setCompId}
                className="w-full sm:w-64"
              />
            </div>
            <div>
              <label className="label">Status</label>
              <Select options={["All Statuses", "Live", "In Progress", "Completed", "Archived", "Upcoming"]} value={statusFilter} onChange={setStatusFilter} className="w-40" />
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setSearch("");
                setStatusFilter("All Statuses");
                setCompId("");
              }}
            >
              Reset
            </Button>
          </div>

          {notice && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-success/30 bg-success-soft px-4 py-2.5 text-[13px] font-semibold text-success">
              <CheckCircle2 size={16} /> {notice}
              <button className="ml-auto font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
            </div>
          )}

          <BulkBar
            count={selection.count}
            noun="match"
            onClear={selection.clear}
            onDelete={() => setPending(matches.filter((m) => selection.isSelected(m.id)))}
          />

          <div className="card mt-4">
            <div className="table-scroll [--table-offset:378px]">
              <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th w-10">
                    <SelectAllBox
                      checked={selection.allSelected}
                      indeterminate={selection.someSelected}
                      onChange={selection.toggleAll}
                      label="Select all matches"
                    />
                  </th>
                  <th className="th">Match Name</th>
                  <th className="th">Event / Competition</th>
                  <th className="th">Type</th>
                  <th className="th">Level</th>
                  <th className="th">Date</th>
                  <th className="th text-center">Clubs</th>
                  <th className="th text-center">Shooters</th>
                  <th className="th text-center">Strings</th>
                  <th className="th text-center" title="Shots on record, against the shots this range declares">
                    Shots
                  </th>
                  <th
                    className="th"
                    title="In Progress until scores are recorded; Completed once they are. A range still to come is Upcoming."
                  >
                    Status
                  </th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {!matches.length && (
                  <tr>
                    <td colSpan={13} className="td py-10 text-center text-muted">
                      No matches yet — import a shoot workbook to populate the portal.
                    </td>
                  </tr>
                )}
                {matches.map((m) => (
                  <tr
                    key={m.id}
                    // Selects the row into the detail panel beside the table.
                    // The panel carries the links out to the results and target.
                    onClick={() => setSelectedId(m.id)}
                    className={`cursor-pointer transition-colors ${
                      detail?.id === m.id ? "bg-primary-soft/60" : selection.isSelected(m.id) ? "bg-primary-soft/50" : "hover:bg-slate-50"
                    }`}
                  >
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      <RowBox checked={selection.isSelected(m.id)} onChange={() => selection.toggle(m.id)} label={`Select ${m.name}`} />
                    </td>
                    <td className="td">
                      <div className="flex items-center gap-3">
                        <Target size={20} className="shrink-0 text-primary" />
                        <div>
                          <span className="font-bold">{m.name}</span>
                          <div className="text-[13px] text-muted">{m.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="td">
                      <div className="font-semibold">{compName(m.competitionId)}</div>
                      <div className="text-[13px] text-muted">{competitionName(m) || dayLabel(m.eventId)}</div>
                      {[m.range, m.stateCode, m.country].filter(Boolean).length > 0 && (
                        <div className="text-[12px] text-muted">
                          {[m.range, m.stateCode, m.country].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="td"><EventTypeChip type={m.eventType} /></td>
                    <td className="td text-slate-600">{m.eventLevel || "—"}</td>
                    <td className="td">
                      <div className="font-semibold">{m.date}</div>
                      {m.startedAt && <div className="text-[13px] text-muted">{m.startedAt}</div>}
                    </td>
                    <td className="td text-center font-semibold">{m.clubs ?? 0}</td>
                    <td className="td text-center font-semibold">{m.shooters ?? 0}</td>
                    <td className="td text-center font-semibold">{m.strings}</td>
                    {/*
                      What was actually shot, against what the range declares. A
                      completed range that reads "48 / 60" has a card missing, and
                      that is worth seeing at a glance rather than by drilling in.
                    */}
                    <td className="td text-center font-semibold">
                      {m.shotsRecorded ?? 0}
                      <span className="font-normal text-muted"> / {m.shots}</span>
                    </td>
                    <td className="td"><Badge tone={statusTone(m.status)}>{m.status}</Badge></td>
                    <td className="td text-right" onClick={(e) => e.stopPropagation()}>
                      <span className="inline-flex items-center gap-1.5">
                        <button onClick={() => openEdit(m)} className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50 hover:text-ink" aria-label="Edit match">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setPending([m])} className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-danger-soft hover:text-danger" aria-label="Delete match">
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="border-t border-line">
              <Pagination summary={`${matches.length} ${matches.length === 1 ? "match" : "matches"}`} />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {detail && (
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-bold text-ink">Match Details</h3>
                <ExternalLink size={15} className="text-muted" />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${detail.status === "LIVE" ? "bg-success" : "bg-slate-300"}`} />
                <span className="font-bold text-ink">{detail.name}</span>
                <Badge tone={statusTone(detail.status)}>{detail.status}</Badge>
              </div>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">
                <li className="flex items-center gap-2.5"><CalendarDays size={15} className="text-muted" /> {detail.date}</li>
                <li className="flex items-center gap-2.5"><PlayCircle size={15} className="text-muted" /> {detail.startedAt}{detail.completedAt ? ` – ${detail.completedAt}` : " – In Progress"}</li>
                <li className="flex items-center gap-2.5"><Trophy size={15} className="text-muted" /> {compName(detail.competitionId)}</li>
                <li className="flex items-center gap-2.5"><Target size={15} className="text-muted" /> {detail.distance}</li>
                <li className="flex items-center gap-2.5"><ListChecks size={15} className="text-muted" /> {detail.strings} Strings</li>
                <li className="flex items-center gap-2.5"><Crosshair size={15} className="text-muted" /> {detail.shots} Shots</li>
              </ul>
              <div className="mt-5 space-y-2.5">
                <Link to={`/competitions/${detail.competitionId}/events/${detail.eventId}/matches/${detail.id}`} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary text-[15px] font-extrabold text-white shadow-sm transition-colors hover:bg-primary-hover">
                  View Results <ExternalLink size={15} />
                </Link>
                <Link to={`/admin/shot-logs?matchId=${detail.id}`} className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-line text-sm font-bold text-ink transition-colors hover:bg-slate-50">
                  View Target & Strings <ListChecks size={14} />
                </Link>
                <button onClick={() => openEdit(detail)} className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-line text-sm font-bold text-ink transition-colors hover:bg-slate-50">
                  Edit Match <Pencil size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="card p-4">
            <h3 className="text-[15px] font-bold text-ink">Quick Actions</h3>
            <div className="mt-3 space-y-2.5">
              <Link to="/admin/import" className="flex h-10 w-full items-center gap-3 rounded-lg border border-line px-3.5 text-sm font-semibold text-ink transition-colors hover:bg-slate-50">
                <Import size={15} className="text-muted" /> Import Results
              </Link>
              {[
                { icon: Tag, label: "Match Types" },
                { icon: LayoutTemplate, label: "String Templates" },
                { icon: RefreshCcw, label: "Bulk Update Status" },
              ].map((a) => (
                <button key={a.label} className="flex h-10 w-full items-center gap-3 rounded-lg border border-line px-3.5 text-sm font-semibold text-ink transition-colors hover:bg-slate-50">
                  <a.icon size={15} className="text-muted" /> {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={editingId ? "Edit Match" : "Create Match"}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy}>
              {busy && <Loader2 size={15} className="animate-spin" />}
              {editingId ? "Save Changes" : "Create Match"}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-4 flex items-center gap-2.5 rounded-lg bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
            <AlertCircle size={16} /> {error}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">Match Name <span className="text-danger">*</span></label>
            <input className="input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Match 6" />
          </div>
          <div>
            <label className="label">Event <span className="text-danger">*</span></label>
            <select className="input appearance-none font-medium" value={form.competitionId} onChange={(e) => set("competitionId", e.target.value)}>
              {competitionOptions.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Event <span className="text-danger">*</span></label>
            <select className="input appearance-none font-medium" value={form.eventId} onChange={(e) => set("eventId", e.target.value)}>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>Event {ev.n}: {ev.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Date</label>
            <input className="input" value={form.date || ""} onChange={(e) => set("date", e.target.value)} placeholder="May 26, 2024" />
          </div>
          <div>
            <label className="label">Distance</label>
            <input className="input" value={form.distance || ""} onChange={(e) => set("distance", e.target.value)} />
          </div>
          <div>
            <label className="label">Shots</label>
            <input type="number" className="input" value={form.shots ?? 10} onChange={(e) => set("shots", Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Strings</label>
            <input type="number" className="input" value={form.strings ?? 2} onChange={(e) => set("strings", Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Status</label>
            <Select options={["UPCOMING", "LIVE", "IN PROGRESS", "COMPLETED", "ARCHIVED"]} value={form.status} onChange={(v) => set("status", v)} />
          </div>
          <div>
            <label className="label">Time Limit</label>
            <input className="input" value={form.timeLimit || ""} onChange={(e) => set("timeLimit", e.target.value)} />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={pending.length > 0}
        busy={removing}
        title={pending.length === 1 ? "Remove this match?" : `Remove ${pending.length} matches?`}
        intro={
          pending.length === 1
            ? "This removes the match from its event, and the shot strings fired at it."
            : "This removes these matches from their events, and the shot strings fired at them."
        }
        items={pending.map((m) => `${m.name}${m.distance ? ` — ${m.distance}` : ""}`)}
        note={
          <>
            The day's aggregate and the competitors' records are recalculated from the ranges that remain. Upload the
            workbook again to rebuild {pending.length === 1 ? "the range" : "the ranges"} from the file — nothing of the old
            data comes back with {pending.length === 1 ? "it" : "them"}. To remove a shoot completely, remove its upload on
            Import History or delete the event.
          </>
        }
        confirmLabel={pending.length === 1 ? "Remove match" : `Remove ${pending.length} matches`}
        onConfirm={removeSelected}
        onCancel={() => setPending([])}
      />
    </div>
  );
}
